import React, { useState, useContext } from "react";
import { 
  Card, CardHeader, Avatar, Chip, Divider, Typography, Box, Stack, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip, 
  styled 
} from '@mui/material';
import axios from "../../api/axios";
import GeneralContext from "../GeneralContext/GeneralContext";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useNavigate } from 'react-router-dom';
import PageButtonWhite from "../GeneralComponents/PageButtonWhite";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import PageButtonSimple from "../GeneralComponents/PageButtonSimple";
import DiferenciaChip from "../GeneralComponents/DiferenciaChip";
import { Download as DownloadIcon } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import DescuentoPeriodoCard from './DescuentoPeriodoCard';
import DescuentoPeriodoCardV2 from './DescuentoPeriodoCardV2';
import CustomAccordion from "../GeneralComponents/CustomAccordion";
import { usePeriodos } from '../utils/utilsRut';
import * as XLSX from "xlsx";

import {ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL, ROL_ADMIN} from "../../Constants/Constants";

const borderStyle = '1px solid #BDBDBD';
const tbPad = 1.2;
const sxEstilos = {
  caja: {
    border: borderStyle,
    borderRadius: 1,
    overflow: 'hidden',
    m: 1,
    mt:0,
  },
  thGray: {
    backgroundColor: '#A8A8A880',
    fontWeight: 'bold',
    border: borderStyle,
    textAlign: 'left',
    p: tbPad,
  },
  thLGray: {
    backgroundColor: '#A8A8A833',
    fontWeight: 'bold',
    border: borderStyle,
    textAlign: 'left',
    p: tbPad,
  },


  thRed: {
    color: '#D84315',
    fontWeight: 'bold',
    border: borderStyle,
    textAlign: 'center',
    p: tbPad,
  },

  thRedL: {
    color: '#D84315',
    border: borderStyle,
    textAlign: 'center',
    p: tbPad,
  },
  tdLabel: {
    fontWeight: 'bold',
    border: borderStyle,
    p: tbPad,
  },
  tdLabelExtra: {
    fontWeight: 'bold',
    border: borderStyle,
    p: tbPad,
    pl: 3,
  },
  tdNormal: {
    border: borderStyle,
    fontWeight: '400',
    textAlign: 'center',
    p: tbPad,
  },
  tdNormalBold: {
    border: borderStyle,
    fontWeight: 'bold',
    textAlign: 'center',
    p: tbPad,
  },
  tdTotal: {
    color: '#D84315',
    fontWeight: 'bold',
    border: borderStyle,
    textAlign: 'center',
    p: tbPad,
  },
  rowLightRed: {
    backgroundColor: '#F8D7D7',
  },
  rowGray: {
    backgroundColor: '#A8A8A880',
  },
};

const BlueTooltip = styled(({ className, ...props }) => (
  <Tooltip arrow placement="top" {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "#003B8DE5", 
    padding: theme.spacing(1.2, 1),
    fontWeight: 500,
  },
  [`& .MuiTooltip-arrow`]: {
    color: "#003B8DE5",
  },
}));

export default function PrefacturaCard({ data, type, fetch, editAccess=false, showZonas=false }) {
  const { includeRol } =  useContext(GeneralContext);
  const navigate = useNavigate();
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);


const {
  periodoActual,
  periodoAnterior,
  periodoSiguiente,
  periodoAnteriorPrev,
  mesAnteriorNombre,
  mesActualNombre,
  mesSiguienteNombre,
  mesAnteriorPrevNombre
} = usePeriodos(data.periodo);


  const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 2, 
  }).format(value);
};


const formatPercentage = (value) => {
  return new Intl.NumberFormat("es-CL", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

  const saldoData = [
  { label: "VALOR ADJUDICADO SIN IVA:", value: formatCurrency(data.valor_adjudicado_sin_iva) },
  { label: "VALOR ADJUDICADO CON IVA:", value: formatCurrency(data.valor_adjudicado_con_iva) },
  { label: "DESCUENTOS MES ACTUAL (NO APLICADO):", value: formatCurrency(data.d_total_n) },
  { label: "DESCUENTOS MES ANTERIOR (APLICADO):", value: formatCurrency(data.d_total_n_menos_1) },
  { label: "VALOR MENSUAL A FACTURAR:", value: formatCurrency(data.valor_mensual_a_facturar_con_iva) },
];

const downloadPDF = (url) => {
    setLoadingPDF(true);
    window.open(url, '_blank');
  };

  const getPreCode = async (query, periodo) => {
    setLoadingButton(true);
    axios
      .getCode(query)
      .then((response) => {
          fetch(periodo)
      })
      .catch((error) => {})
      .finally(() => {


        setLoadingButton(false);
      });

}

const downloadXLS = () => {
  if (!data?.info_zonas || data.info_zonas.length === 0) {
    alert("No hay datos de zonas para exportar.");
    return;
  }

  const rows = data.info_zonas.map((zona) => {
    const valorAdjudicado = zona.actual.valor_adjudicado_con_iva || 0;
    const descuento1 = zona.actual.d_total_n_menos_2 || 0;
    const descuento2 = zona.actual.d_total_n_menos_1 || 0;
    const facturado = (zona.actual.valor_mensual_a_facturar_con_iva || 0) - descuento1 - descuento2;

    return {
      "Zona": zona.nombre_zona,
      "Valor Adjudicado con IVA (proporcional)": zona.actual.valor_mensual_a_facturar_con_iva || 0,
      [`Descuento 1 (${mesAnteriorPrevNombre})`]: descuento1,
      [`Descuento 2 (${mesAnteriorNombre})`]: descuento2,
      [`Facturado periodo actual ${data.periodo} (${mesActualNombre})`]: facturado,
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Zonas");

  XLSX.writeFile(workbook, `detalle_zonas_${data.nombre_operador}_${data.periodo}.xlsx`);
};



  return (
    <Card sx={{ marginRight: 0, marginBottom: 2, padding: 1, pt: 0, borderRadius: 2 }}>
       <CardHeader
    sx={{ padding: 1.5, pt: 0, borderBottom: "1px solid #0000001F" }}
    avatar={
      <Box sx={{pt: 1.5}}>
      <Avatar
        src={data.avatar}
        sx={{ bgcolor: 'primary.main', borderRadius: 2, width: 60, height: 60 }}
      /></Box>
    }
    title={
      <Box display="flex" flexDirection="column" sx={{pt: 1.5}}>
        <Typography variant="subtitle1" fontWeight="bold">{data.nombre_operador}</Typography>
        <Box display="inline-flex" alignItems="center" gap={1}>
          <Chip label={"Facturación"} size="small" sx={{ backgroundColor: "#3D6CAE", color: "#FFF" }} />
          <Chip label={"En Regimen"} size="small" sx={{ backgroundColor: "#3D6CAE33" }} />
        </Box>

          {includeRol([ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL, ROL_ADMIN]) ? (
        <Box><PageButtonSimple
        width="auto"
        pad="0"
        fs="0.6875rem"
        onClick={(e) => {
          navigate("/prefactura_detalle/" + data.id_operador+"/"+data.periodo);
        }}
      >
        Ver detalle operador
      </PageButtonSimple></Box>) : null}
      </Box>
    }
    action={
      <Grid container sx={{ height: "100%", marginBottom: "-8px", marginTop: "8px" }}>
        <Grid
          sx={{
            borderLeft: "1px solid #0000001F",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingY: 0
          }}
        >
        <Stack>
          <PageButtonSimple
            disabled={!data.link_prefactura_sagec}
            width="10rem"
            pad="0.5rem"
            fs="0.6875rem"
            startIcon={<DownloadIcon />}
            onClick={(e) => {
              e.stopPropagation();
              downloadPDF(data.link_prefactura_sagec);
            }}
          >
            <Stack>
            <span>SAGEC prefactura</span>
            <BlueTooltip title={`Fecha en que se generó la prefactura SAGEC`}>
            <span><b>{data.version ? "V"+data.version : ""}</b> - {data.fecha_creacion ? data.fecha_creacion.split("T")[0] : ""}</span>
            </BlueTooltip>
            </Stack>
          </PageButtonSimple>

          <Box sx={{borderTop: "1px solid #0000001F", p: 1}}>
            <BlueTooltip title={`Contingencias pendientes relacionadas al descuento 1`}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "default" }}>
        <Typography variant="small" component="span" sx={{fontSize: "0.8rem"}} >
          Contingencias pendientes:
        </Typography>

        <Chip
          label={data.numero_contingencias_pendientes !== null && data.numero_contingencias_pendientes !== undefined
  ? data.numero_contingencias_pendientes
  : "??"}
          sx={{
            fontWeight: 600,
            color: "#fff", height: "20px",
            bgcolor: !data.numero_contingencias_pendientes || data.numero_contingencias_pendientes === 0 ? "#ABABB5" : "#D84315",
            "& .MuiChip-label": { px: 1.2, py: "2px", fontSize: "0.6875rem" },
          }}
        />
      </Box>
    </BlueTooltip>
            </Box>
            </Stack>
        </Grid>
        <Grid
          sx={{
            borderLeft: "1px solid #0000001F",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingY: 1.5,
            paddingX: 1,
          }}
        >
          <PageButtonSimple
            disabled={!data.link_prefactura_operador}
            width="10rem"
            pad="0.5rem"
            fs="0.6875rem"
            startIcon={<DownloadIcon />}
            onClick={(e) => {
              e.stopPropagation();
              downloadPDF(data.link_prefactura_operador);
            }}
          > 
            <Stack>
            <span>Operador prefactura</span>
            <BlueTooltip title={`Fecha en que se recibió la prefactura enviada por el operador`}>
            <span>{ data.fecha_creacion_operador ? data.fecha_creacion_operador.split("T")[0] : "---"}</span>
            </BlueTooltip>
            </Stack>
          </PageButtonSimple>
        </Grid>
        
        <Grid
          sx={{
            borderLeft: "1px solid #0000001F",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            justifyContent: "space-between",
            paddingY: 1.5,
            paddingX: 1,
          }}
        > 

          <Typography variant="body2" color="textSecondary" sx={{marginX: "0.5rem"}}><b>Periodo: {data.periodo}</b></Typography>
          <Typography variant="body2" color="textSecondary" component="div" >
            <Box display="flex" alignItems="center" mr={1}>
              <FiberManualRecordIcon
                sx={{
                  fontSize: ".75rem",
                  color:
                    data.estado_validacion === "En Apelación"
              ? "#F9961E" : data.estado_validacion === "Pendiente"
                ? "#FFE263"
                : data.estado_validacion.startsWith("Rechazado")
                ? "#FB3B3B"
                : "#41AE43",
                  marginX: "0.5rem",
                }}
              />
              {data.estado_validacion}
            </Box>
          </Typography>
          <DiferenciaChip data={data}/>
        </Grid>
      </Grid>
    }
  />

<Grid container spacing={1} sx={{ mt: 2 }} columns={20}>
  <Grid size={{ xs: 20, sm: 20, md: 13 }} sx={{ minWidth: 0, borderRight: "1px solid #B4B4B4", pr: 2, pt:0 }}>
    <Box sx={{ ...sxEstilos.caja, width: '100%' }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
  <TableRow>
    <TableCell
      sx={{
        ...sxEstilos.thLGray,
        border: borderStyle,
        fontWeight: 'bold',
        borderRight: borderStyle,
      }}
    >
      Monto adjudicado con IVA (proporcional)
    </TableCell>
    <TableCell
      sx={{ ...sxEstilos.tdNormal, border: borderStyle }}
    >
      {formatCurrency(data.anterior_prev?.valor_mensual_a_facturar_con_iva || 0)}
    </TableCell>
    <TableCell
      sx={{ ...sxEstilos.tdNormal, border: borderStyle }}
    >
      {formatCurrency(data.anterior?.valor_mensual_a_facturar_con_iva || 0)}
    </TableCell>
    <TableCell rowSpan={2} sx={{...sxEstilos.rowGray, border: borderStyle, textAlign: 'center', fontWeight: 'bold',}}>
      Total <br/> (dcto. 1 + dcto. 2)
    </TableCell>
  </TableRow>
  <TableRow>
    <TableCell sx={{ ...sxEstilos.tdLabel, ...sxEstilos.thGray }} >
      Detalle descuentos
    </TableCell>
    <TableCell sx={{ ...sxEstilos.thRed, ...sxEstilos.rowGray }}>
      Descuento 1 ({mesAnteriorPrevNombre})
    </TableCell>
    <TableCell sx={{ ...sxEstilos.thRed, ...sxEstilos.rowGray }}>
      Descuento 2 ({mesAnteriorNombre})
    </TableCell>
  </TableRow>
</TableHead>

          <TableBody>
<TableRow>
  <TableCell sx={{ ...sxEstilos.tdLabel, ...sxEstilos.thLGray }}>Monto descuento totales</TableCell>
  <TableCell sx={{...sxEstilos.tdNormal, ...sxEstilos.thRedL}}>
    {formatCurrency(data.anterior_prev?.monto_descuento_totales_operador || 0)}
  </TableCell>
  <TableCell sx={{...sxEstilos.tdNormal, ...sxEstilos.thRedL}}>
    {formatCurrency(data.anterior?.monto_descuento_totales_operador || 0)}
  </TableCell>
  <TableCell sx={sxEstilos.tdNormalBold}>
    {formatCurrency(
      (data.anterior_prev?.monto_descuento_totales_operador || 0) +
        (data.anterior?.monto_descuento_totales_operador || 0)
    )}
  </TableCell>
</TableRow>
<TableRow>
  <TableCell sx={{ ...sxEstilos.tdLabel, ...sxEstilos.thLGray }}>Monto descuento cubierto por contingencia</TableCell>
  <TableCell sx={{...sxEstilos.tdNormal, ...sxEstilos.thRedL}}>
    {formatCurrency(data.anterior_prev?.monto_descuento_cubierto_contingencia_operador || 0)}
  </TableCell>
  <TableCell sx={{...sxEstilos.tdNormal, ...sxEstilos.thRedL}}>
    {formatCurrency(data.anterior?.monto_descuento_cubierto_contingencia_operador || 0)}
  </TableCell>
  <TableCell sx={sxEstilos.tdNormalBold}>
    {formatCurrency(
      (data.anterior_prev?.monto_descuento_cubierto_contingencia_operador || 0) +
        (data.anterior?.monto_descuento_cubierto_contingencia_operador || 0)
    )}
  </TableCell>
</TableRow>
<TableRow>
  <TableCell sx={{ ...sxEstilos.tdLabelExtra, ...sxEstilos.thLGray }}><Box sx={{ display: "flex", alignItems: "flex-start", pl: 1 }}>
    <Box sx={{ mt: "3px", mr: 1, fontWeight: "bold" }}>•</Box>
    <Box fontWeight="bold">Monto contingencia Aprobadas</Box>
  </Box></TableCell>
  <TableCell sx={{...sxEstilos.tdNormal, ...sxEstilos.thRedL}}>
    {formatCurrency(data.anterior_prev?.monto_contingencia_aprobadas_operador || 0)}
  </TableCell>
  <TableCell sx={{...sxEstilos.tdNormal, ...sxEstilos.thRedL}}>
    {formatCurrency(data.anterior?.monto_contingencia_aprobadas_operador || 0)}
  </TableCell>
  <TableCell sx={sxEstilos.tdNormalBold}>
    {formatCurrency(
      (data.anterior_prev?.monto_contingencia_aprobadas_operador || 0) +
        (data.anterior?.monto_contingencia_aprobadas_operador || 0)
    )}
  </TableCell>
</TableRow>
<TableRow>
  <TableCell sx={{ ...sxEstilos.tdLabelExtra, ...sxEstilos.thLGray }}><Box sx={{ display: "flex", alignItems: "flex-start", pl: 1 }}>
    <Box sx={{ mt: "3px", mr: 1, fontWeight: "bold" }}>•</Box>
    <Box fontWeight="bold">Monto contingencia Pendientes</Box>
  </Box></TableCell>
  <TableCell sx={{...sxEstilos.tdNormal, ...sxEstilos.thRedL}}>
    {formatCurrency(data.anterior_prev?.monto_contingencia_pendientes_operador || 0)}
  </TableCell>
  <TableCell sx={{...sxEstilos.tdNormal, ...sxEstilos.thRedL}}>
    {formatCurrency(data.anterior?.monto_contingencia_pendientes_operador || 0)}
  </TableCell>
  <TableCell sx={sxEstilos.tdNormalBold}>
    {formatCurrency(
      (data.anterior_prev?.monto_contingencia_pendientes_operador || 0) +
        (data.anterior?.monto_contingencia_pendientes_operador || 0)
    )}
  </TableCell>
</TableRow>
<TableRow>
  <TableCell sx={{ ...sxEstilos.tdLabelExtra, ...sxEstilos.thLGray }}><Box sx={{ display: "flex", alignItems: "flex-start", pl: 1 }}>
    <Box sx={{ mt: "3px", mr: 1, fontWeight: "bold" }}>•</Box>
    <Box fontWeight="bold">Monto contingencia Rechazadas</Box>
  </Box></TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.anterior_prev?.monto_contingencia_rechazadas_operador || 0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.anterior?.monto_contingencia_rechazadas_operador || 0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormalBold }}>
    {formatCurrency(
      (data.anterior_prev?.monto_contingencia_rechazadas_operador || 0) +
        (data.anterior?.monto_contingencia_rechazadas_operador || 0)
    )}
  </TableCell>
</TableRow>
<TableRow>
  <TableCell sx={{ ...sxEstilos.tdLabel, ...sxEstilos.thLGray }}>Monto descuentos (sin contingencias)</TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.anterior_prev?.monto_descuentos_sin_contingencia_operador || 0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.anterior?.monto_descuentos_sin_contingencia_operador || 0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormalBold }}>
    {formatCurrency(
      (data.anterior_prev?.monto_descuentos_sin_contingencia_operador || 0) +
        (data.anterior?.monto_descuentos_sin_contingencia_operador || 0)
    )}
  </TableCell>
</TableRow>
<TableRow>
  <TableCell sx={{ ...sxEstilos.tdLabel, ...sxEstilos.thLGray }}>Monto descuento calculado</TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.anterior_prev?.monto_efectivo_operador || 0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.anterior?.monto_efectivo_operador || 0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormalBold}}>
    {formatCurrency(
      (data.anterior_prev?.monto_efectivo_operador || 0) + (data.anterior?.monto_efectivo_operador || 0)
    )}
  </TableCell>
</TableRow>

<TableRow>
  <TableCell sx={{ ...sxEstilos.tdLabel, ...sxEstilos.thLGray }}>Monto descontado prefactura anterior</TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.anterior_validado?.d_total_n_menos_1 || (data.anterior_prev?.monto_efectivo_operador || 0))}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormalBold }}>
    {formatCurrency((data.anterior_validado?.d_total_n_menos_1 || (data.anterior_prev?.monto_efectivo_operador || 0)) )}
  </TableCell>
</TableRow>

<TableRow>
  <TableCell sx={{ ...sxEstilos.tdLabel, ...sxEstilos.thLGray }}>Monto reajuste</TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.anterior_prev?.ajuste_calculo_anterior * -1 || 0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.anterior?.ajuste_calculo_anterior * -1 || 0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormalBold }}>
    {formatCurrency((data.anterior_prev?.ajuste_calculo_anterior * -1 || 0) + (data.anterior?.ajuste_calculo_anterior * -1 || 0))}
  </TableCell>
</TableRow>

<TableRow>
  <TableCell sx={{ ...sxEstilos.tdLabel, ...sxEstilos.thLGray }}>Descuento efectivo</TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.d_total_n_menos_2 || 0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}>
    {formatCurrency(data.d_total_n_menos_1 || 0)}
  </TableCell>
  <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.rowLightRed, ...sxEstilos.thRed }}>
    {formatCurrency((data.d_total_n_menos_2 || 0) + (data.d_total_n_menos_1 || 0))}
  </TableCell>
</TableRow>

          </TableBody>
        </Table>
      </TableContainer>
    </Box>

  <Box sx={{ pt: 2, width: '100%', minWidth: 0 }}>
    <CustomAccordion
      descripcion={`DESCUENTO 3 (${mesActualNombre}) ${periodoActual}:`}
      monto={formatCurrency(data.d_total_n)}
      contenido={{
        normal: `Este monto corresponde al total de descuentos registrados en (${mesActualNombre}) y`,
        bold: ` será descontado efectivamente el próximo periodo. Este descuento puede ser justificado llenando un formulario de Contingencia antes que termine (${mesSiguienteNombre}), de lo contrario será aplicado en su totalidad.`
      }}
    />
  </Box>
  </Grid>

  <Grid size={{ xs: 20, sm: 20, md: 7 }} sx={{ minWidth: 0 }}>
    <DescuentoPeriodoCardV2 data={data} editAccess={editAccess} fetch={fetch} />
  </Grid>
</Grid>

  
    {showZonas && (
     <Box sx={{ width: "100%", minWidth: "100%", display: "block" }}>
      <Grid container spacing={2} alignItems="stretch" sx={{ mt: 4, mb: 2, px: 1 }}>
       <Grid container sx={{border: borderStyle, pt: 2,  width: "100%", display: "block"}}>
      <Grid size={12} sx={{mb: 2}}>
        <Typography variant="h6" sx={{padding: 0, pt: 0, fontFamily: '"Roboto", "Helvetica"', textAlign: "center" }}>
                      <b>DETALLE FACTURA POR ZONA</b>
            </Typography>
      </Grid>


      <Grid size={12} sx={{mb: 0}}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...sxEstilos.tdNormalBold, ...sxEstilos.rowGray }} >Zona</TableCell>
            <TableCell sx={{ ...sxEstilos.tdNormalBold, ...sxEstilos.rowGray }} align="right">Valor Adjudicado con IVA (proporcional)</TableCell>
            <TableCell sx={{ ...sxEstilos.thRed, ...sxEstilos.rowGray }} align="right">Descuento 1 ({mesAnteriorPrevNombre})</TableCell>
            <TableCell sx={{ ...sxEstilos.thRed, ...sxEstilos.rowGray }}align="right">Descuento 2 ({mesAnteriorNombre})</TableCell>
            <TableCell sx={{ ...sxEstilos.tdNormalBold, ...sxEstilos.rowGray }} align="right">Facturado periodo actual {data.periodo} ({mesActualNombre})</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.info_zonas.map((zona, index) => (
            <TableRow key={index}>
              <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thLGray }}>{zona.nombre_zona}</TableCell>
              <TableCell sx={{ ...sxEstilos.tdNormal }} align="right">{formatCurrency(zona.actual.monto_total_zona || 0)}</TableCell>
              <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}  align="right">{formatCurrency(zona.actual.d_total_n_menos_2 || 0)}</TableCell>
              <TableCell sx={{ ...sxEstilos.tdNormal, ...sxEstilos.thRedL }}  align="right">{formatCurrency(zona.actual.d_total_n_menos_1 || 0)}</TableCell>
              <TableCell sx={{ ...sxEstilos.tdNormal }}  align="right">{formatCurrency(zona.actual.valor_mensual_a_facturar_con_iva - zona.actual.d_total_n_menos_2 - zona.actual.d_total_n_menos_1 || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </Grid>
      </Grid>
      <Grid size={8} sx={{mb: 0}}>
      </Grid>
      <Grid size={4} sx={{mb: 0}}>
      <PageButtonWhite
        loading={data.info_zonas.length === 0}
        onClick={downloadXLS}
        width="100%"
      >
        Descargar .xls de detalle por zona
      </PageButtonWhite>

      </Grid>
      </Grid>
      </Box>
      )}


    </Card>
  );
}
